import React, { FC, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import Tooltip from '@/components/tooltip';
import { Icons } from '@/components/icons';
import { cn } from "@/utils/cn";

export interface DropdownOption {
  label: string;
  value: string;
  meta?: any;
}

export interface NetworkDropdownProps {
  label: string;
  tooltip?: string;
  options: DropdownOption[];
  value?: DropdownOption;
  onChange: (option: DropdownOption) => void;
  noCapitalize?: boolean;
  disabled?: boolean;
}

const NetworkDropdown: FC<NetworkDropdownProps> = ({
  label,
  tooltip,
  options,
  value,
  onChange,
  noCapitalize,
  disabled,
}) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-text-secondary text-xl tracking-02em leading-6 gap-1 flex items-center">
        {label}
        {tooltip && (
          <Tooltip iconColor="text-text-secondary">
            <span>{tooltip}</span>
          </Tooltip>
        )}
      </h1>
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild disabled={disabled}>
          <button
            className={cn(
              "bg-surface-secondary items-center px-4 rounded-lg h-[50px] w-full flex justify-between text-text-primary",
              disabled ? "opacity-50" : ""
            )}
          >
            <span
              className={cn(
                "font-medium leading-18 truncate",
                !noCapitalize && "capitalize"
              )}
            >
              {value?.label}
            </span>
            <Icons.chevronDown
              className={cn(
                "ml-2 transition-transform duration-200",
                open && "rotate-180"
              )}
            />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            sideOffset={1}
            className="w-[368px] dropdown-shadow bg-surface-secondary z-[100] rounded-lg overflow-hidden divide-y divide-border-primary"
          >
            {options.map((option, index) => (
              <DropdownMenu.Item
                key={index}
                className="text-text-primary focus:outline-none leading-18 px-4  hover:bg-surface-primary transition-colors cursor-pointer"
                onClick={() => onChange(option)}
              >
                <div className='flex flex-col gap-1 min-h-[60px] justify-center'>
                  <div className=' flex items-center justify-between'>
                    {option.value === "custom" ? (
                      <>
                        <div className="flex flex-col py-4 gap-1">
                          <span
                            className={cn(
                              "truncate",
                              !noCapitalize && "capitalize"
                            )}
                          >
                            {option.label}
                          </span>
                          <span className="text-text-secondary text-sm leading-4">
                            We won&apos;t be able to issue UNITS to you if you
                            customize your guardian
                          </span>
                        </div>
                        {value?.value === option.value && (
                          <Icons.check className="size-6 ml-2 shrink-0" />
                        )}
                      </>
                    ) : (
                      <>
                        <span
                          className={cn("truncate", !noCapitalize && "capitalize")}
                        >
                          {option.label}
                        </span>
                        {value?.value === option.value && (
                          <Icons.check className="size-6 ml-2" />
                        )}
                      </>
                    )}
                  </div>
                  {option.meta && (
                    <div className='text-sm text-gray-100'>
                      {/* PubKey: {renderMetaPubkey(option.meta?.pub || '')} */}
                      URL: {option.value}
                    </div>
                  )}
                </div>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
};

export default NetworkDropdown;
